import React, {
  FC,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import style from './style.module.less';
import { ContextMenu } from '@law-connect/react-components';
import PlusIcon from './plus.svg?react';
import OfflineIcon from './offline.svg?react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import selectors from '../../../redux/selectors';
import { actions } from '../../../redux/slices';
import { Avatar, Typography } from '@galaxy-ds/core';
import { ActionModal } from '../../../components/action-modal';
import { UserInfoModal } from '../../../components/user-info-modal';
import { UserAvatarComponent } from '../../../components/user-avatar';
import {
  RequestPrivateInfoLink,
  RequestPrivateInfoModal,
  RequestPrivateInfoState,
} from '../../../components/request-modal';
import { MessageType } from '@law-connect/types';
import { getRequestedUserInfo } from '../../../utils/requested-user-info';
import { steps } from '../../../components/onboarding/steps';
import { MatterCreatedToast } from '../../../components/matter-created-toast';
import { useTranslation } from 'react-i18next';
import { ContactInfo } from '../../../components/contact-info';

const HEADER_TIMER_DELAY = 300;
const FLOATING_GAP = 64;

interface Props {
  connectionId: string;
  chatBodyRef: RefObject<HTMLDivElement>;
}

export const ChatHeader: FC<Props> = (props) => {
  // we need to pass in the chat width and set that as the floating header width because cannot use relative width with a fixed position
  const { chatBodyRef, connectionId } = props;
  const { t } = useTranslation();
  const userImageRef = useRef<HTMLDivElement>(null);
  const [showFloating, setShowFloating] = useState(false);
  const updateTimer = useRef<NodeJS.Timeout | null>(null);
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectors.lawyerConnection.getUser(connectionId));
  const [isEndChatModalOpen, setIsEndChatModalOpen] = useState(false);
  const [isDeleteChatModalOpen, setIsDeleteChatModalOpen] = useState(false);
  const [isUserInfoModalOpen, setIsUserInfoModalOpen] = useState(false);
  const [requestUserInfoModalOpen, setRequestUserInfoModalOpen] =
    useState(false);
  const [isConvertingToMatterTriggered, setIsConvertingToMatterTriggered] =
    useState(false);
  const [isCreateMatterModalOpen, setIsCreateMatterModalOpen] = useState(false);
  const isConvertingToMatter = useAppSelector(
    selectors.prematter.isConvertingToMatter()
  );

  const connection = useAppSelector(
    selectors.lawyerConnection.getConnectionById(connectionId)
  );
  const messages = useAppSelector(
    selectors.lawyerConnection.getMessagesById(connectionId)
  );

  const contactInfoPending = useMemo(() => {
    if (messages?.length) {
      // user hasnt replied and
      return (
        !user?.phone &&
        messages.some(
          (m) =>
            m.type === MessageType.ContactInformation &&
            m.from === 'lawyer' &&
            !messages.some(
              (nm) =>
                nm.type === MessageType.ContactInformation &&
                nm?.content?.requestMessageId === m.id
            )
        )
      );
    } else {
      return false;
    }
  }, [messages, user?.phone]);

  const openEndChatModal = useCallback(() => setIsEndChatModalOpen(true), []);
  const closeEndChatModal = useCallback(() => setIsEndChatModalOpen(false), []);
  const closeDeleteChatModal = useCallback(
    () => setIsDeleteChatModalOpen(false),
    []
  );

  const openUserInfoModal = useCallback(() => {
    if (connection?.permissions?.userDetails) {
      setIsUserInfoModalOpen(true);
    }
  }, [connection?.permissions?.userDetails]);
  const closeUserInfoModal = useCallback(
    () => setIsUserInfoModalOpen(false),
    []
  );

  const openRequestModal = useCallback(
    () => setRequestUserInfoModalOpen(true),
    []
  );
  const closeRequestModal = useCallback(
    () => setRequestUserInfoModalOpen(false),
    []
  );
  const closeCreateMatterModal = useCallback(
    () => setIsCreateMatterModalOpen(false),
    []
  );

  // Add new state for contact info modal
  const [isContactInfoModalOpen, setIsContactInfoModalOpen] = useState(false);

  // Add handlers for contact info modal
  const openRequestContactInfoModal = useCallback(
    () => setIsContactInfoModalOpen(true),
    []
  );
  const closeRequestContactInfoModal = useCallback(
    () => setIsContactInfoModalOpen(false),
    []
  );

  // Add handler for contact info request
  const onRequestContactInfo = useCallback(() => {
    dispatch(
      actions.lawyerConnection.requestContactInformation({ id: connectionId })
    );
    closeRequestContactInfoModal();
  }, [dispatch, connectionId, closeRequestContactInfoModal]);

  useEffect(() => {
    dispatch(actions.lawyerConnection.fetchUser({ connectionId }));
  }, [dispatch, connectionId]);

  useEffect(() => {
    if (
      !contactInfoPending &&
      !user?.phone &&
      connection?.permissions?.phoneNumber
    ) {
      dispatch(actions.lawyerConnection.fetchUser({ connectionId }));
    }
  }, [
    connection?.permissions?.phoneNumber,
    connectionId,
    contactInfoPending,
    dispatch,
    user?.phone,
  ]);

  const onEndChat = useCallback(() => {
    dispatch(actions.lawyerConnection.markEnded({ id: connectionId }));
    closeEndChatModal();
  }, [dispatch, connectionId, closeEndChatModal]);

  const onDeleteChat = useCallback(() => {
    dispatch(actions.lawyerConnection.markDeleted({ id: connectionId }));
    closeDeleteChatModal();
  }, [dispatch, connectionId, closeDeleteChatModal]);

  const createMatterAllowed = useMemo(
    () =>
      connection?.permissions?.userDetails && !connection?.convertedToMatterAt,
    [connection?.permissions?.userDetails, connection?.convertedToMatterAt]
  );

  const convertToMatter = useCallback(() => {
    if (!isConvertingToMatter && createMatterAllowed) {
      dispatch(actions.prematter.convertToMatter({ id: connectionId }));
      setIsConvertingToMatterTriggered(true);
    }
  }, [dispatch, connectionId, isConvertingToMatter, createMatterAllowed]);

  useEffect(() => {
    if (isConvertingToMatterTriggered && connection?.convertedToMatterAt) {
      setIsCreateMatterModalOpen(true);
      setIsConvertingToMatterTriggered(false);
    }
  }, [isConvertingToMatterTriggered, connection?.convertedToMatterAt]);

  // TODO: on deleted chat we want to find a way to navigate back and close the window???

  const onConfirmRequest = useCallback(() => {
    dispatch(
      actions.lawyerConnection.requestPermission({
        id: connection.id,
      })
    );
    closeRequestModal();
  }, [dispatch, connection?.id, closeRequestModal]);

  // if leap not using ie 11 then we can use the intersection observer instead of timer

  useEffect(() => {
    if (updateTimer?.current) {
      clearInterval(updateTimer.current);
    }
    updateTimer.current = setInterval(() => {
      if (userImageRef.current) {
        const { top } = userImageRef.current.getBoundingClientRect();
        if (top > -FLOATING_GAP) {
          setShowFloating(false);
        } else {
          setShowFloating(true);
        }
      }
    }, HEADER_TIMER_DELAY);
  }, []);

  const requestedUserInfo = useMemo(
    () => getRequestedUserInfo(messages, connection),
    [messages, connection]
  );

  const userInfoState = useMemo(() => {
    if (!connection) {
      return;
    }
    if (connection?.permissions?.userDetails) {
      // user permissions allowed
      return RequestPrivateInfoState.ACCEPTED;
    } else if (requestedUserInfo) {
      return RequestPrivateInfoState.PENDING;
    } else {
      return RequestPrivateInfoState.REQUEST;
    }
  }, [connection, requestedUserInfo]);

  if (!user) {
    return null;
  }

  const userName = `${user.firstName || ''}${
    user.firstName?.length && user.lastName?.length ? ' ' : ''
  }${user.lastName || ''}`.trim() || 'Client';

  return (
    <>
      <div
        className={`${style.floatingContainer} 
          ${showFloating ? style.showAll : ''}`}
      >
        <div
          className={`${style.userInfo} ${
            connection?.permissions?.userDetails ? '' : style.readOnly
          }`}
        >
          <UserAvatarComponent
            user={user}
            userImageClassName={style.userImage}
          />
          <Typography variant='h2'>{userName}</Typography>
          <ContactInfo
            user={user}
            onRequestContact={openRequestContactInfoModal}
            contactInfoPending={contactInfoPending}
            permissions={connection?.permissions}
          />
        </div>
        <div className={style.contextMenuContainer}>
          {connection?.endedAt || connection?.deletedAt ? null : (
            <ContextMenu
              items={[
                {
                  label: t('lawyer.create-matter'),
                  onClick: convertToMatter,
                  icon: <PlusIcon />,
                  disabled: !createMatterAllowed,
                },
                {
                  label: t('lawyer.end-chat.title'),
                  onClick: openEndChatModal,
                  icon: <OfflineIcon />,
                },
              ]}
            />
          )}
        </div>
      </div>

      <div
        className={`${style.headerContainer} ${
          connection?.permissions?.userDetails ? '' : style.readOnly
        }`}
        ref={userImageRef}
      >
        {/* if the scroll is past the headerContainer then we show the floating header */}
        <UserAvatarComponent user={user} userImageClassName={style.userImage} />
        <Typography variant='h1'>{userName}</Typography>
        <ContactInfo
          user={user}
          onRequestContact={openRequestContactInfoModal}
          contactInfoPending={contactInfoPending}
          permissions={connection?.permissions}
        />
      </div>

      <ActionModal
        isOpen={isEndChatModalOpen}
        onClose={closeEndChatModal}
        title={t('lawyer.end-chat.title')}
        confirm={{
          text: t('lawyer.end-chat.title'),
          onClick: onEndChat,
        }}
      >
        {t('lawyer.end-chat.description')}
      </ActionModal>
      <ActionModal
        isOpen={isDeleteChatModalOpen}
        onClose={closeDeleteChatModal}
        title={t('lawyer.delete-chat.title')}
        confirm={{
          text: t('lawyer.delete'),
          onClick: onDeleteChat,
        }}
      >
        {t('lawyer.delete-chat.description')}
      </ActionModal>
      <RequestPrivateInfoModal
        closeRequestModal={closeRequestModal}
        isOpenRequestModal={requestUserInfoModalOpen}
        onConfirmRequest={onConfirmRequest}
      />
      <UserInfoModal
        closeModal={closeUserInfoModal}
        isOpen={isUserInfoModalOpen}
        user={user}
      />
      <MatterCreatedToast
        isOpen={isCreateMatterModalOpen}
        onClose={closeCreateMatterModal}
      />
      <ActionModal
        isOpen={isContactInfoModalOpen}
        onClose={closeRequestContactInfoModal}
        title={t('lawyer.request-contact-info.title')}
        confirm={{
          text: t('lawyer.send'),
          onClick: onRequestContactInfo,
        }}
      >
        {t('lawyer.request-contact-info.description')}
      </ActionModal>
    </>
  );
};
